import {useEffect, useRef} from "react";

import {authSucceeded} from "../../auth/actions/auth_succeeded_event";
import {emailSignUp} from "../../auth/api/email_sign_up";
import {getSmsLoginError, isSmsLoginAccountMismatchError, usePostSmsLoginInitMutation} from "../../auth/api/sms_login";
import {closeFlow} from "../../auth/state/registration_flow_slice";
import {AuthActionType} from "../../auth/types/AuthActionType";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addNotification} from "../../notifications/state/notifications_slice";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {gtmEventApplicationRegister} from "../../tracking/google_tag_manager/gtm_event_application_register";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {getIsUserAuthenticatedApi} from "../../user/api/get_is_user_authenticated";
import {getUserProfileApi, useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {usePatchUserProfileMutation} from "../../user/api/patch_user_profile";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {IUserProfile} from "../../user/types/IUserProfile";
import {UserAccountType} from "../../user/types/UserAccountType";
import {isValidationError} from "../../utils/isValidationError";
import {ApplicationModalStateEvent, IApplicationModalStateContext} from "../reducers/application_modal_state_transitions";
import {IApplicationModalState, IApplicationModalStateData} from "../reducers/application_modal_step_reducer";
import {setContinuePromotionFlowAfterAuth, setPromotionLeadDraft} from "../state/main_lead_slice";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {IApplicationData} from "../types/IApplicationData";
import {IPromotionLeadDraft} from "../types/lead/IPromotionLeadDraft";
import {useSendMainLead} from "./use_send_main_lead";

interface IProps {
    handleModalClose: () => void;
    modalState: IApplicationModalState;
    onStandardLeadSuccess?: (sentApplication: IApplicationData | null) => Promise<void>;
    sendModalEvent: (
        event: ApplicationModalStateEvent,
        dataUpdates?: Partial<IApplicationModalStateData>,
        contextUpdates?: Partial<IApplicationModalStateContext>
    ) => void;
}

export const usePromotionLeadFlow = (props: IProps) => {
    const {handleModalClose, modalState, onStandardLeadSuccess, sendModalEvent} = props;
    const dispatch = useAppDispatch();
    const sendLead = useSendMainLead();
    const [postSmsLoginInit] = usePostSmsLoginInitMutation();

    const mainLead = useAppSelector((state) => state.mainLead);
    const registrationFlow = useAppSelector((state) => state.registrationFlow);
    const isAuthenticated = useIsAuthenticated();
    const {data: userProfile} = useGetUserProfileQuery();
    const [patchUserProfile] = usePatchUserProfileMutation();

    const authFlowWasOpenRef = useRef(false);
    const startedAuthenticatedRef = useRef(false);
    const authActionDuringPromotionFlowRef = useRef<AuthActionType | null>(null);
    const registerEventSentRef = useRef(false);
    const draftSourceFormStepRef = useRef<ApplicationModalStep>(ApplicationModalStep.PROMOTION_FORM);

    const isPromotionFlow = mainLead.flowVariant === "promotion";
    const getDraftPhone = (draft: IPromotionLeadDraft) => `${draft.phone.code} ${draft.phone.number}`;
    const getDraftSmsLoginPhone = (draft: IPromotionLeadDraft) => `+${draft.phone.code}${draft.phone.number.replace(/\D/g, "")}`;
    const normalizePhone = (phone: string | null | undefined) => phone?.replace(/\D/g, "") || "";
    const isDraftPhoneVerifiedForProfile = (draft: IPromotionLeadDraft, profile: IUserProfile | null | undefined) =>
        Boolean(profile?.phone_verified && normalizePhone(profile.phone) === normalizePhone(getDraftPhone(draft)));
    const getSmsLoginMismatchMessage = (name: string) => {
        const firstName = name.trim().split(/\s+/)[0];
        const messageWithoutName =
            "Masz już u nas konto, ale wpisany numer telefonu jest inny niż podawany wcześniej. Popraw numer, aby kontynuować, lub podaj inny adres e-mail, by założyć nowe konto.";
        const messageWithName =
            "masz już u nas konto, ale wpisany numer telefonu jest inny niż podawany wcześniej. Popraw numer, aby kontynuować, lub podaj inny adres e-mail, by założyć nowe konto.";

        return firstName ? `${firstName}, ${messageWithName}` : messageWithoutName;
    };

    const syncDraftToUserProfile = async (draft: IPromotionLeadDraft, profile = userProfile, shouldSkipSync = false) => {
        if (shouldSkipSync) {
            return profile;
        }

        const draftPhone = getDraftPhone(draft);
        const shouldPatchProfile = profile?.name !== draft.name || normalizePhone(profile?.phone) !== normalizePhone(draftPhone);

        if (!shouldPatchProfile) {
            return profile;
        }

        await patchUserProfile({
            name: draft.name,
            phone: draftPhone
        }).unwrap();

        return await dispatch(getUserProfileApi.endpoints.getUserProfile.initiate(undefined, {forceRefetch: true})).unwrap();
    };

    const sendDraftLead = async (draft: IPromotionLeadDraft) => {
        const {vendorId: _vendorId, offerId: _offerId, propertyId: _propertyId, source: _source, sourceSection: _sourceSection, ...leadFormValues} = draft;

        return await sendLead(leadFormValues, () => handleModalClose(), isPromotionFlow ? {persistFormValues: false} : undefined);
    };

    const finalizeStandardLead = async (draft: IPromotionLeadDraft) => {
        const application = await sendDraftLead(draft);

        if (onStandardLeadSuccess) {
            await onStandardLeadSuccess(application || null);
            return;
        }

        handleModalClose();
    };

    const finalizePromotionLead = async (draft: IPromotionLeadDraft) => {
        sendModalEvent({type: "PROMOTION_SUBMIT_STARTED"});

        try {
            const application = await sendDraftLead(draft);

            if (
                application &&
                !application.user_created &&
                !startedAuthenticatedRef.current &&
                authActionDuringPromotionFlowRef.current === AuthActionType.REGISTRATION &&
                !registerEventSentRef.current
            ) {
                registerEventSentRef.current = true;
                gtmEventApplicationRegister();
            }

            const shouldShowPromotionSuccessModal = !startedAuthenticatedRef.current && authActionDuringPromotionFlowRef.current !== AuthActionType.LOGIN;

            if (!shouldShowPromotionSuccessModal) {
                handleModalClose();
                return;
            }

            sendModalEvent({type: "PROMOTION_SUBMIT_SUCCEEDED"}, {originalApplicationResponse: application || null});
        } catch {
            dispatch(addFailureNotification("Nie udało się wysłać zgłoszenia."));
            sendModalEvent({type: "PROMOTION_SUBMIT_FAILED"});
        }
    };

    const finalizeLeadAfterAuthentication = async (draft: IPromotionLeadDraft) => {
        if (isPromotionFlow) {
            await finalizePromotionLead(draft);
            return;
        }

        await finalizeStandardLead(draft);
    };

    const getSourceFormStep = () => (isPromotionFlow ? undefined : draftSourceFormStepRef.current);
    const getEditPhoneEvent = (): ApplicationModalStateEvent => {
        const step = getSourceFormStep();

        return step !== undefined ? {type: "PROMOTION_EDIT_PHONE", step} : {type: "PROMOTION_EDIT_PHONE"};
    };
    const sendSubmitFailedEvent = () => {
        const step = getSourceFormStep();
        sendModalEvent(step !== undefined ? {type: "PROMOTION_SUBMIT_FAILED", step} : {type: "PROMOTION_SUBMIT_FAILED"});
    };

    const continueLeadAfterAuthentication = async (profile: IUserProfile) => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        try {
            if (isDraftPhoneVerifiedForProfile(draft, profile)) {
                await finalizeLeadAfterAuthentication(draft);
                return;
            }

            await syncDraftToUserProfile(draft, profile, false);
            sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
        } catch (_error) {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            sendSubmitFailedEvent();
        }
    };

    const handleDraftSubmit = async (values: IApplicationFormValues) => {
        startedAuthenticatedRef.current = isAuthenticated;
        authActionDuringPromotionFlowRef.current = null;
        registerEventSentRef.current = false;
        draftSourceFormStepRef.current = modalState.step;

        const draft: IPromotionLeadDraft = {
            ...values,
            vendorId: mainLead.vendorId || 0,
            offerId: mainLead.offerId,
            propertyId: mainLead.propertyId,
            source: mainLead.source!,
            sourceSection: mainLead.sourceSection!
        };

        dispatch(setPromotionLeadDraft(draft));

        if (isAuthenticated && userProfile) {
            try {
                if (isDraftPhoneVerifiedForProfile(draft, userProfile)) {
                    await finalizeLeadAfterAuthentication(draft);
                    return;
                }

                await syncDraftToUserProfile(draft, userProfile);
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }

            return;
        }

        if (isAuthenticated) {
            try {
                await syncDraftToUserProfile(draft, userProfile);
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }

            return;
        }

        try {
            await emailSignUp({
                email: draft.email,
                name: draft.name,
                phone: getDraftPhone(draft),
                type: UserAccountType.LOCAL,
                source: UserSource.REGISTRATION,
                marketing_email: draft.marketing_email,
                marketing_sms: draft.marketing_sms
            });

            dispatch(
                authSucceeded({
                    type: AuthActionType.REGISTRATION,
                    userSource: UserSource.REGISTRATION,
                    registrationMethod: "email"
                })
            );
            authActionDuringPromotionFlowRef.current = AuthActionType.REGISTRATION;
            dispatch(setContinuePromotionFlowAfterAuth(true));
        } catch (registrationError) {
            if (isValidationError(registrationError) && registrationError.appError.fieldErrors.phone?.length) {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: null,
                    smsLoginInitialSendResult: null
                });
                throw registrationError;
            }

            let response;

            try {
                response = await postSmsLoginInit({
                    email: draft.email,
                    phone: getDraftSmsLoginPhone(draft)
                });
            } catch {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: null,
                    smsLoginInitialSendResult: null
                });
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
                return;
            }

            if (!response.error) {
                sendModalEvent(
                    {type: "PROMOTION_SMS_LOGIN_REQUIRED"},
                    {
                        smsLoginErrorMessage: null,
                        smsLoginInitialSendResult: {}
                    }
                );
                return;
            }

            const error = getSmsLoginError(response.error, "Nie udało się wysłać kodu SMS.");

            if (isSmsLoginAccountMismatchError(error)) {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: getSmsLoginMismatchMessage(draft.name),
                    smsLoginInitialSendResult: null
                });
                return;
            }

            sendModalEvent(getEditPhoneEvent(), {
                smsLoginErrorMessage: null,
                smsLoginInitialSendResult: null
            });
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
        }
    };

    const handleSmsLoginRejected = (_error: {message: string; status: number}) => {
        sendModalEvent(getEditPhoneEvent(), {
            smsLoginErrorMessage: getSmsLoginMismatchMessage(mainLead.promotionLeadDraft?.name || ""),
            smsLoginInitialSendResult: null
        });
    };

    const handleSmsLoginSuccess = async () => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        authActionDuringPromotionFlowRef.current = AuthActionType.LOGIN;

        dispatch(
            authSucceeded({
                type: AuthActionType.LOGIN,
                userSource: UserSource.REGISTRATION,
                registrationMethod: "sms"
            })
        );

        try {
            const isUserAuthenticated = await dispatch(
                getIsUserAuthenticatedApi.endpoints.getIsUserAuthenticated.initiate(undefined, {forceRefetch: true})
            ).unwrap();

            if (!isUserAuthenticated) {
                throw new Error("SMS login did not create an authenticated session");
            }

            const profile = await dispatch(getUserProfileApi.endpoints.getUserProfile.initiate(undefined, {forceRefetch: true})).unwrap();
            if (!profile) {
                throw new Error("SMS login did not return a user profile");
            }

            await syncDraftToUserProfile(draft, profile);
            await finalizeLeadAfterAuthentication(draft);
        } catch (error) {
            notifyBugsnag(error, "Lead SMS login post-authentication failed", {
                stage: "sms_login_post_auth",
                vendorId: draft.vendorId,
                offerId: draft.offerId,
                propertyId: draft.propertyId
            });
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            sendSubmitFailedEvent();
        }
    };

    const handlePhoneVerificationSuccess = async () => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        await finalizeLeadAfterAuthentication(draft);
    };

    const handlePhoneVerificationChangeNumber = () => {
        sendModalEvent(getEditPhoneEvent(), {
            smsLoginErrorMessage: null,
            smsLoginInitialSendResult: null
        });
    };

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth) {
            authFlowWasOpenRef.current = false;
            return;
        }

        if (registrationFlow.isActive) {
            authFlowWasOpenRef.current = true;
            return;
        }

        if (!authFlowWasOpenRef.current || modalState.step !== ApplicationModalStep.PROMOTION_AUTH_FALLBACK || registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        dispatch(setContinuePromotionFlowAfterAuth(false));
        handleModalClose();
    }, [dispatch, handleModalClose, mainLead.continuePromotionFlowAfterAuth, modalState.step, registrationFlow.isActive, registrationFlow.lastAuthAction]);

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth || !isAuthenticated || !userProfile) {
            return;
        }

        if (modalState.step === ApplicationModalStep.PROMOTION_AUTH_FALLBACK && !registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        if (registrationFlow.lastAuthAction) {
            authActionDuringPromotionFlowRef.current = registrationFlow.lastAuthAction;
        }
        dispatch(setContinuePromotionFlowAfterAuth(false));
        if (registrationFlow.isActive) {
            dispatch(closeFlow());
        }
        void continueLeadAfterAuthentication(userProfile);
    }, [
        dispatch,
        isAuthenticated,
        mainLead.continuePromotionFlowAfterAuth,
        modalState.step,
        registrationFlow.isActive,
        registrationFlow.lastAuthAction,
        userProfile
    ]);

    return {
        handleDraftSubmit,
        handlePhoneVerificationSuccess,
        handlePhoneVerificationChangeNumber,
        handleSmsLoginRejected,
        handleSmsLoginSuccess
    };
};
import {useEffect, useRef} from "react";

import {authSucceeded} from "../../auth/actions/auth_succeeded_event";
import {emailSignUp} from "../../auth/api/email_sign_up";
import {getSmsLoginError, isSmsLoginAccountMismatchError, usePostSmsLoginInitMutation} from "../../auth/api/sms_login";
import {closeFlow} from "../../auth/state/registration_flow_slice";
import {AuthActionType} from "../../auth/types/AuthActionType";
import {notifyBugsnag} from "../../errors/bugsnag/notify_bugsnag";
import {addNotification} from "../../notifications/state/notifications_slice";
import {addFailureNotification} from "../../notifications/utils/add_failure_notification";
import {defaultAPIErrorNotification} from "../../notifications/utils/error_notifications";
import {useAppDispatch, useAppSelector} from "../../store/hooks";
import {gtmEventApplicationRegister} from "../../tracking/google_tag_manager/gtm_event_application_register";
import {UserSource} from "../../tracking/tracking_utils/user_source";
import {getIsUserAuthenticatedApi} from "../../user/api/get_is_user_authenticated";
import {getUserProfileApi, useGetUserProfileQuery} from "../../user/api/get_user_profile";
import {usePatchUserProfileMutation} from "../../user/api/patch_user_profile";
import {useIsAuthenticated} from "../../user/hooks/use_is_authenticated";
import {IUserProfile} from "../../user/types/IUserProfile";
import {UserAccountType} from "../../user/types/UserAccountType";
import {isValidationError} from "../../utils/isValidationError";
import {ApplicationModalStateEvent, IApplicationModalStateContext} from "../reducers/application_modal_state_transitions";
import {IApplicationModalState, IApplicationModalStateData} from "../reducers/application_modal_step_reducer";
import {setContinuePromotionFlowAfterAuth, setPromotionLeadDraft} from "../state/main_lead_slice";
import {ApplicationModalStep} from "../types/ApplicationModalStep";
import {IApplicationFormValues} from "../types/form/IApplicationFormValues";
import {IApplicationData} from "../types/IApplicationData";
import {IPromotionLeadDraft} from "../types/lead/IPromotionLeadDraft";
import {useSendMainLead} from "./use_send_main_lead";

interface IProps {
    handleModalClose: () => void;
    modalState: IApplicationModalState;
    onStandardLeadSuccess?: (sentApplication: IApplicationData | null) => Promise<void>;
    sendModalEvent: (
        event: ApplicationModalStateEvent,
        dataUpdates?: Partial<IApplicationModalStateData>,
        contextUpdates?: Partial<IApplicationModalStateContext>
    ) => void;
}

export const usePromotionLeadFlow = (props: IProps) => {
    const {handleModalClose, modalState, onStandardLeadSuccess, sendModalEvent} = props;
    const dispatch = useAppDispatch();
    const sendLead = useSendMainLead();
    const [postSmsLoginInit] = usePostSmsLoginInitMutation();

    const mainLead = useAppSelector((state) => state.mainLead);
    const registrationFlow = useAppSelector((state) => state.registrationFlow);
    const isAuthenticated = useIsAuthenticated();
    const {data: userProfile} = useGetUserProfileQuery();
    const [patchUserProfile] = usePatchUserProfileMutation();

    const authFlowWasOpenRef = useRef(false);
    const startedAuthenticatedRef = useRef(false);
    const authActionDuringPromotionFlowRef = useRef<AuthActionType | null>(null);
    const registerEventSentRef = useRef(false);
    const draftSourceFormStepRef = useRef<ApplicationModalStep>(ApplicationModalStep.PROMOTION_FORM);

    const isPromotionFlow = mainLead.flowVariant === "promotion";
    const getDraftPhone = (draft: IPromotionLeadDraft) => `${draft.phone.code} ${draft.phone.number}`;
    const getDraftSmsLoginPhone = (draft: IPromotionLeadDraft) => `+${draft.phone.code}${draft.phone.number.replace(/\D/g, "")}`;
    const normalizePhone = (phone: string | null | undefined) => phone?.replace(/\D/g, "") || "";
    const isDraftPhoneVerifiedForProfile = (draft: IPromotionLeadDraft, profile: IUserProfile | null | undefined) =>
        Boolean(profile?.phone_verified && normalizePhone(profile.phone) === normalizePhone(getDraftPhone(draft)));
    const getSmsLoginMismatchMessage = (name: string) => {
        const firstName = name.trim().split(/\s+/)[0];
        const messageWithoutName =
            "Masz już u nas konto, ale wpisany numer telefonu jest inny niż podawany wcześniej. Popraw numer, aby kontynuować, lub podaj inny adres e-mail, by założyć nowe konto.";
        const messageWithName =
            "masz już u nas konto, ale wpisany numer telefonu jest inny niż podawany wcześniej. Popraw numer, aby kontynuować, lub podaj inny adres e-mail, by założyć nowe konto.";

        return firstName ? `${firstName}, ${messageWithName}` : messageWithoutName;
    };

    const syncDraftToUserProfile = async (draft: IPromotionLeadDraft, profile = userProfile, shouldSkipSync = false) => {
        if (shouldSkipSync) {
            return profile;
        }

        const draftPhone = getDraftPhone(draft);
        const shouldPatchProfile = profile?.name !== draft.name || normalizePhone(profile?.phone) !== normalizePhone(draftPhone);

        if (!shouldPatchProfile) {
            return profile;
        }

        await patchUserProfile({
            name: draft.name,
            phone: draftPhone
        }).unwrap();

        return await dispatch(getUserProfileApi.endpoints.getUserProfile.initiate(undefined, {forceRefetch: true})).unwrap();
    };

    const sendDraftLead = async (draft: IPromotionLeadDraft) => {
        const {vendorId: _vendorId, offerId: _offerId, propertyId: _propertyId, source: _source, sourceSection: _sourceSection, ...leadFormValues} = draft;

        return await sendLead(leadFormValues, () => handleModalClose(), isPromotionFlow ? {persistFormValues: false} : undefined);
    };

    const finalizeStandardLead = async (draft: IPromotionLeadDraft) => {
        const application = await sendDraftLead(draft);

        if (onStandardLeadSuccess) {
            await onStandardLeadSuccess(application || null);
            return;
        }

        handleModalClose();
    };

    const finalizePromotionLead = async (draft: IPromotionLeadDraft) => {
        sendModalEvent({type: "PROMOTION_SUBMIT_STARTED"});

        try {
            const application = await sendDraftLead(draft);

            if (
                application &&
                !application.user_created &&
                !startedAuthenticatedRef.current &&
                authActionDuringPromotionFlowRef.current === AuthActionType.REGISTRATION &&
                !registerEventSentRef.current
            ) {
                registerEventSentRef.current = true;
                gtmEventApplicationRegister();
            }

            const shouldShowPromotionSuccessModal = !startedAuthenticatedRef.current && authActionDuringPromotionFlowRef.current !== AuthActionType.LOGIN;

            if (!shouldShowPromotionSuccessModal) {
                handleModalClose();
                return;
            }

            sendModalEvent({type: "PROMOTION_SUBMIT_SUCCEEDED"}, {originalApplicationResponse: application || null});
        } catch {
            dispatch(addFailureNotification("Nie udało się wysłać zgłoszenia."));
            sendModalEvent({type: "PROMOTION_SUBMIT_FAILED"});
        }
    };

    const finalizeLeadAfterAuthentication = async (draft: IPromotionLeadDraft) => {
        if (isPromotionFlow) {
            await finalizePromotionLead(draft);
            return;
        }

        await finalizeStandardLead(draft);
    };

    const getSourceFormStep = () => (isPromotionFlow ? undefined : draftSourceFormStepRef.current);
    const getEditPhoneEvent = (): ApplicationModalStateEvent => {
        const step = getSourceFormStep();

        return step !== undefined ? {type: "PROMOTION_EDIT_PHONE", step} : {type: "PROMOTION_EDIT_PHONE"};
    };
    const sendSubmitFailedEvent = () => {
        const step = getSourceFormStep();
        sendModalEvent(step !== undefined ? {type: "PROMOTION_SUBMIT_FAILED", step} : {type: "PROMOTION_SUBMIT_FAILED"});
    };

    const continueLeadAfterAuthentication = async (profile: IUserProfile) => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        try {
            if (isDraftPhoneVerifiedForProfile(draft, profile)) {
                await finalizeLeadAfterAuthentication(draft);
                return;
            }

            await syncDraftToUserProfile(draft, profile, false);
            sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
        } catch (_error) {
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            sendSubmitFailedEvent();
        }
    };

    const handleDraftSubmit = async (values: IApplicationFormValues) => {
        startedAuthenticatedRef.current = isAuthenticated;
        authActionDuringPromotionFlowRef.current = null;
        registerEventSentRef.current = false;
        draftSourceFormStepRef.current = modalState.step;

        const draft: IPromotionLeadDraft = {
            ...values,
            vendorId: mainLead.vendorId || 0,
            offerId: mainLead.offerId,
            propertyId: mainLead.propertyId,
            source: mainLead.source!,
            sourceSection: mainLead.sourceSection!
        };

        dispatch(setPromotionLeadDraft(draft));

        if (isAuthenticated && userProfile) {
            try {
                if (isDraftPhoneVerifiedForProfile(draft, userProfile)) {
                    await finalizeLeadAfterAuthentication(draft);
                    return;
                }

                await syncDraftToUserProfile(draft, userProfile);
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }

            return;
        }

        if (isAuthenticated) {
            try {
                await syncDraftToUserProfile(draft, userProfile);
                sendModalEvent({type: "PROMOTION_PHONE_VERIFICATION_REQUIRED"});
            } catch (_error) {
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
            }

            return;
        }

        try {
            await emailSignUp({
                email: draft.email,
                name: draft.name,
                phone: getDraftPhone(draft),
                type: UserAccountType.LOCAL,
                source: UserSource.REGISTRATION,
                marketing_email: draft.marketing_email,
                marketing_sms: draft.marketing_sms
            });

            dispatch(
                authSucceeded({
                    type: AuthActionType.REGISTRATION,
                    userSource: UserSource.REGISTRATION,
                    registrationMethod: "email"
                })
            );
            authActionDuringPromotionFlowRef.current = AuthActionType.REGISTRATION;
            dispatch(setContinuePromotionFlowAfterAuth(true));
        } catch (registrationError) {
            if (isValidationError(registrationError) && registrationError.appError.fieldErrors.phone?.length) {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: null,
                    smsLoginInitialSendResult: null
                });
                throw registrationError;
            }

            let response;

            try {
                response = await postSmsLoginInit({
                    email: draft.email,
                    phone: getDraftSmsLoginPhone(draft)
                });
            } catch {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: null,
                    smsLoginInitialSendResult: null
                });
                dispatch(addNotification({notification: defaultAPIErrorNotification}));
                return;
            }

            if (!response.error) {
                sendModalEvent(
                    {type: "PROMOTION_SMS_LOGIN_REQUIRED"},
                    {
                        smsLoginErrorMessage: null,
                        smsLoginInitialSendResult: {}
                    }
                );
                return;
            }

            const error = getSmsLoginError(response.error, "Nie udało się wysłać kodu SMS.");

            if (isSmsLoginAccountMismatchError(error)) {
                sendModalEvent(getEditPhoneEvent(), {
                    smsLoginErrorMessage: getSmsLoginMismatchMessage(draft.name),
                    smsLoginInitialSendResult: null
                });
                return;
            }

            sendModalEvent(getEditPhoneEvent(), {
                smsLoginErrorMessage: null,
                smsLoginInitialSendResult: null
            });
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
        }
    };

    const handleSmsLoginRejected = (_error: {message: string; status: number}) => {
        sendModalEvent(getEditPhoneEvent(), {
            smsLoginErrorMessage: getSmsLoginMismatchMessage(mainLead.promotionLeadDraft?.name || ""),
            smsLoginInitialSendResult: null
        });
    };

    const handleSmsLoginSuccess = async () => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        authActionDuringPromotionFlowRef.current = AuthActionType.LOGIN;

        dispatch(
            authSucceeded({
                type: AuthActionType.LOGIN,
                userSource: UserSource.REGISTRATION,
                registrationMethod: "sms"
            })
        );

        try {
            const isUserAuthenticated = await dispatch(
                getIsUserAuthenticatedApi.endpoints.getIsUserAuthenticated.initiate(undefined, {forceRefetch: true})
            ).unwrap();

            if (!isUserAuthenticated) {
                throw new Error("SMS login did not create an authenticated session");
            }

            const profile = await dispatch(getUserProfileApi.endpoints.getUserProfile.initiate(undefined, {forceRefetch: true})).unwrap();
            if (!profile) {
                throw new Error("SMS login did not return a user profile");
            }

            await syncDraftToUserProfile(draft, profile);
            await finalizeLeadAfterAuthentication(draft);
        } catch (error) {
            notifyBugsnag(error, "Lead SMS login post-authentication failed", {
                stage: "sms_login_post_auth",
                vendorId: draft.vendorId,
                offerId: draft.offerId,
                propertyId: draft.propertyId
            });
            dispatch(addNotification({notification: defaultAPIErrorNotification}));
            sendSubmitFailedEvent();
        }
    };

    const handlePhoneVerificationSuccess = async () => {
        const draft = mainLead.promotionLeadDraft;

        if (!draft) {
            handleModalClose();
            return;
        }

        await finalizeLeadAfterAuthentication(draft);
    };

    const handlePhoneVerificationChangeNumber = () => {
        sendModalEvent(getEditPhoneEvent(), {
            smsLoginErrorMessage: null,
            smsLoginInitialSendResult: null
        });
    };

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth) {
            authFlowWasOpenRef.current = false;
            return;
        }

        if (registrationFlow.isActive) {
            authFlowWasOpenRef.current = true;
            return;
        }

        if (!authFlowWasOpenRef.current || modalState.step !== ApplicationModalStep.PROMOTION_AUTH_FALLBACK || registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        dispatch(setContinuePromotionFlowAfterAuth(false));
        handleModalClose();
    }, [dispatch, handleModalClose, mainLead.continuePromotionFlowAfterAuth, modalState.step, registrationFlow.isActive, registrationFlow.lastAuthAction]);

    useEffect(() => {
        if (!mainLead.continuePromotionFlowAfterAuth || !isAuthenticated || !userProfile) {
            return;
        }

        if (modalState.step === ApplicationModalStep.PROMOTION_AUTH_FALLBACK && !registrationFlow.lastAuthAction) {
            return;
        }

        authFlowWasOpenRef.current = false;
        if (registrationFlow.lastAuthAction) {
            authActionDuringPromotionFlowRef.current = registrationFlow.lastAuthAction;
        }
        dispatch(setContinuePromotionFlowAfterAuth(false));
        if (registrationFlow.isActive) {
            dispatch(closeFlow());
        }
        void continueLeadAfterAuthentication(userProfile);
    }, [
        dispatch,
        isAuthenticated,
        mainLead.continuePromotionFlowAfterAuth,
        modalState.step,
        registrationFlow.isActive,
        registrationFlow.lastAuthAction,
        userProfile
    ]);

    return {
        handleDraftSubmit,
        handlePhoneVerificationSuccess,
        handlePhoneVerificationChangeNumber,
        handleSmsLoginRejected,
        handleSmsLoginSuccess
    };
};
